import { useEffect, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/teams-app-internal-analytics/use-analytics-events';

import {
	countRenderedActions,
	firedAt,
	getErrorAttributes,
	type CardRequestErrorAttributes,
} from '../analytics';
import { useCardOpenTimer } from '../store/cardOpenTimer';
import type { PersonProfileData } from '../types';

// Cast payload superset to `never` so `fireEvent` accepts extra SLO fields.
const asExtendedPayload = <T>(payload: T): never => payload as never;

/**
 * Returns a callback that fires `operational.profilecard.triggered.request`.
 * Call from the same handler that starts the timer (popup `onOpen`) so the
 * SLO denominator lines up with the wall-clock interaction.
 */
export function useFireTriggeredRequest(): () => void {
	const { fireEvent } = useAnalyticsEvents();
	return () => {
		fireEvent('operational.profilecard.triggered.request', { firedAt: firedAt() });
	};
}

/**
 * Fires `ui.profilecard.rendered.spinner` once when the loading fallback mounts.
 */
export function useFireRenderedSpinner(): void {
	const { fireEvent } = useAnalyticsEvents();
	const firedRef = useRef(false);
	useEffect(() => {
		if (firedRef.current) {
			return;
		}
		firedRef.current = true;
		fireEvent('ui.profilecard.rendered.spinner', { firedAt: firedAt() });
	}, [fireEvent]);
}

/**
 * Fires `operational.profilecard.succeeded.request` once when profile data
 * arrives, with `duration` measured from the opening interaction.
 */
export function useFireRequestSucceeded(hasProfileData: boolean): void {
	const { fireEvent } = useAnalyticsEvents();
	const { getDurationSinceInteraction } = useCardOpenTimer();
	const firedRef = useRef(false);
	useEffect(() => {
		if (!hasProfileData || firedRef.current) {
			return;
		}
		firedRef.current = true;
		fireEvent('operational.profilecard.succeeded.request', {
			firedAt: firedAt(),
			duration: getDurationSinceInteraction() ?? 0,
		});
	}, [hasProfileData, fireEvent, getDurationSinceInteraction]);
}

/**
 * Fires `ui.profilecard.rendered.content` once on mount, with `duration` from
 * interaction to first content paint and `numActions` for the header actions.
 */
export function useFireContentRendered(profileData: PersonProfileData | undefined): void {
	const { fireEvent } = useAnalyticsEvents();
	const { getDurationSinceInteraction } = useCardOpenTimer();
	const firedRef = useRef(false);
	const profileDataRef = useRef(profileData);
	profileDataRef.current = profileData;
	useEffect(() => {
		if (firedRef.current) {
			return;
		}
		firedRef.current = true;
		fireEvent('ui.profilecard.rendered.content', {
			firedAt: firedAt(),
			duration: getDurationSinceInteraction() ?? 0,
			numActions: countRenderedActions(profileDataRef.current),
		});
	}, [fireEvent, getDurationSinceInteraction]);
}

/**
 * Fires the terminal no-data event once. NotFound is emitted as
 * `succeeded.request` (the request itself succeeded); everything else as
 * `failed.request`. `null` is treated as NotFound. Pass `undefined` to opt out.
 */
export function useFireRequestFailed(error: unknown): void {
	const { fireEvent } = useAnalyticsEvents();
	const { getDurationSinceInteraction } = useCardOpenTimer();
	const firedRef = useRef(false);
	useEffect(() => {
		if (error === undefined || firedRef.current) {
			return;
		}
		firedRef.current = true;
		const attributes: CardRequestErrorAttributes =
			error === null ? { errorType: 'NotFound' } : getErrorAttributes(error);
		if (attributes.errorType === 'NotFound') {
			fireEvent('operational.profilecard.succeeded.request', {
				firedAt: firedAt(),
				duration: getDurationSinceInteraction() ?? 0,
			});
			return;
		}
		fireEvent(
			'operational.profilecard.failed.request',
			asExtendedPayload({
				firedAt: firedAt(),
				duration: getDurationSinceInteraction() ?? 0,
				// schema requires `errorMessage`; send the classified type to avoid PII.
				errorMessage: attributes.errorType,
				errorType: attributes.errorType,
				...(attributes.statusCode !== undefined ? { errorStatusCode: attributes.statusCode } : {}),
			}),
		);
	}, [error, fireEvent, getDurationSinceInteraction]);
}
